import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Play, Plus } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { getFacilityById } from '../../lib/services/facilities'
import { getAssignments } from '../../lib/services/visits'
import { listFacilityContacts } from '../../lib/services/facilityContacts'
import { presentationStatusFromVisit, presentationStatusLabel } from '../../lib/presentationStatus'
import { fieldSurveyorSurveyActionLabel, fieldSurveyorSurveyDestination } from '../../lib/fieldSurveyorSurveyRouting'

type Tab = 'contacts' | 'surveys'

export function FacilityDetailPage() {
  const { facilityId } = useParams<{ facilityId: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('contacts')

  const {
    data: facility,
    isLoading,
    isError,
    isFetched,
  } = useQuery({
    queryKey: ['facility', facilityId],
    queryFn: () => getFacilityById(facilityId!),
    enabled: !!facilityId,
    retry: false,
  })

  // Fail closed: do not hydrate contacts/survey chrome until facility is authorized for the viewer.
  const facilityReady = Boolean(facilityId && facility && !isError)

  const { data: visits = [] } = useQuery({
    queryKey: ['assignments'],
    queryFn: getAssignments,
    enabled: facilityReady,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['facility-contacts', facilityId],
    queryFn: () => listFacilityContacts(facilityId!),
    enabled: facilityReady,
  })

  const facilityVisits = visits.filter((v) => v.facility_id === facilityId)
  const address = facility
    ? [facility.address, facility.city, facility.state, facility.zip_code].filter(Boolean).join(', ')
    : ''

  if (isFetched && (!facility || isError)) {
    return (
      <FieldAppShell eyebrow="Facility" title="Not available" backTo="/facilities">
        <div className="fs-surface px-4 py-8 text-center text-sm text-slate-500">
          This facility is not available for your assignments.
        </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell
      eyebrow={facility?.facility_name ?? 'Facility'}
      title="Facility"
      backTo="/facilities"
    >
      <div className="space-y-3">
        {/* About card + primary CTA */}
        <div className="fs-surface p-3.5">
          <p className="font-brand text-[15px] font-bold leading-tight text-[var(--brand-deep)]">
            {isLoading ? 'Loading…' : facility?.facility_name}
          </p>
          {address && <p className="mt-0.5 text-[11px] text-slate-500">{address}</p>}
          {(facility?.facility_type || facility?.license_number) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {facility?.facility_type && (
                <span className="brand-chip">{facility.facility_type}</span>
              )}
              {facility?.license_number && (
                <span className="fs-tag fs-tag-gray">License {facility.license_number}</span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate(`/visits/new?facilityId=${facilityId}`)}
            className="fs-btn-primary mt-3"
          >
            <Play size={15} />
            Start survey
          </button>
        </div>

        {/* Tabs */}
        <div className="fs-seg" role="tablist" aria-label="Facility detail">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'contacts'}
            className={tab === 'contacts' ? 'active' : ''}
            onClick={() => setTab('contacts')}
          >
            Contacts ({contacts.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'surveys'}
            className={tab === 'surveys' ? 'active' : ''}
            onClick={() => setTab('surveys')}
          >
            Past surveys ({facilityVisits.length})
          </button>
        </div>

        {tab === 'contacts' && (
          <>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate(`/facilities/${facilityId}/contacts/new`)}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
              >
                <Plus size={12} />
                Add contact
              </button>
            </div>

            {contacts.length === 0 ? (
              <div className="fs-surface px-4 py-8 text-center text-sm text-slate-500">
                No contacts yet. Add one to enable PDF delivery.
              </div>
            ) : (
              <div className="fs-surface overflow-hidden">
                {contacts.map((c) => (
                  <div key={c.contact_id} className="fs-row cursor-default hover:bg-transparent">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="fs-row-title">{c.contact_name}</p>
                        {c.is_primary && <span className="fs-tag fs-tag-blue">Primary</span>}
                      </div>
                      <p className="fs-row-meta mt-0.5">
                        {c.role_label && <>{c.role_label}<br /></>}
                        {c.email}
                        {c.phone && <> · {c.phone}</>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="fs-note">
              Contacts are <strong>not platform users</strong>. They receive PDF reports by email.
              No login, no portal.
            </p>
          </>
        )}

        {tab === 'surveys' && (
          facilityVisits.length === 0 ? (
            <div className="fs-surface px-4 py-8 text-center text-sm text-slate-500">
              No surveys yet.
            </div>
          ) : (
            <div className="fs-surface overflow-hidden">
              {facilityVisits.map((v) => {
                const bucket = presentationStatusFromVisit(v.status)
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => navigate(fieldSurveyorSurveyDestination(v))}
                    className="fs-row w-full"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="fs-row-title">{v.visit_date}</p>
                      <p className="fs-row-meta mt-0.5">
                        {v.surveyor_name && <>{v.surveyor_name} · </>}
                        {v.visit_type !== 'unknown' && <span className="capitalize">{v.visit_type.replace(/_/g, ' ')}</span>}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`fs-tag fs-tag-${bucket}`}>
                        {presentationStatusLabel(bucket)}
                      </span>
                      <span className="text-[10px] font-black text-[var(--brand-primary)]">
                        {fieldSurveyorSurveyActionLabel(v)}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )
        )}
      </div>
    </FieldAppShell>
  )
}
